import { FormFieldsGeneratorService } from './../customer-form/form-helpers/form-fields-generator.service';
import { Inject, Injectable } from '@angular/core';
import { LoadersService } from '../utils/loaders/loaders.service';
import RecaptchaService from '../customer-form/recaptcha/recaptcha.service';
import { CheckoutService } from './checkout.service';
import { CommonInfoService } from '../cart-process/common-info.service';
import { INipBlacklisted } from '../interfaces/nip-blacklisted.interface';
import { CommonModalTemplatesEnum } from '../utils/modals/common-modal-templates.enum';
import { NotificationProviderService, NotificationProviderTypeEnum } from '../external-interfaces/notification-provider.service';
import { CustomerDetailsModel } from '../models/customer-details.model';
import { IdTypeEnum } from '../models/cartDetails/idType.enum';

@Injectable()
export class SubmitOrderService {
  private isRequestPending = false;
  private lastFormData: string;
  private idCardType: IdTypeEnum;

  constructor(
    @Inject('Endpoint') private endpoint: any,
    private checkoutService: CheckoutService,
    private loadersService: LoadersService,
    private recaptchaService: RecaptchaService,
    private commonInfo: CommonInfoService,
    private notificationProvider: NotificationProviderService,
    private formsGenerator: FormFieldsGeneratorService,
  ) {
    this.notificationProvider.subscribe(
      NotificationProviderTypeEnum.CUSTOMER_DETAILS,
      (customerData: CustomerDetailsModel) => {
        if (customerData) {
          this.idCardType = customerData.idCardType;
        }
      });
  }

  public resolveData(): { name: string, value: string }[] {
  const activeForm = this.formsGenerator.activeForm?.controls;
  const formData = [];

  Object.keys(activeForm).map(item => {
    if ((activeForm[item] as any).controls) {
      Object.keys((activeForm[item] as any).controls).map(childItem => {
        if ((activeForm[item] as any).controls[childItem].value) {
          formData.push({
            name: (activeForm[item] as any).controls[childItem].name,
            value: (activeForm[item] as any).controls[childItem].value
          });
        }
      });
    } else if (activeForm[item].value) {
      formData.push({
        name: (activeForm[item] as any).name,
        value: activeForm[item].value
      });
    }
  });

  const captchaEl = document.querySelector('input[id="g-recaptcha-mock"]') as HTMLInputElement;
  const captchaValue = captchaEl?.value || this.recaptchaService.recaptchaResponse;

  if (captchaValue) {
    formData.push({
      name: 'g-recaptcha-response',
      value: captchaValue
    });
  }

  return formData;
}

  public submitOrder(): void {
    this.showModal();

    if (this.isRequestPending) {
      return;
    }

    this.isRequestPending = true;

    const urlParams = window.location.search;
    const endpointName = this.commonInfo.isSecondPage ? 'save.submitSecondStep' : 'save.submitThirdStep';

    this.endpoint(endpointName, this.getSerializedData())
      .run()
      .then((response: any) => {
        const body = response.body;

        if (response.status === 200) {
          if (body.hasOwnProperty('url')) {
            window.location.href = body.url;
          } else {
            this.checkoutService.controlOrderProgress();
          }
        }
      })
      .catch((err: any) => {
        console.log(err);
        window.location.href = '/cart/multi/error' + urlParams;
      });
  }

  public saveOrder(): void {
    const formData = JSON.stringify(this.resolveData());

    if (formData === this.lastFormData) {
      return;
    }

    const endpointName = this.commonInfo.isSecondPage
      ? 'save.saveSecondStep'
      : 'save.saveThirdStep';

    this.endpoint(endpointName, this.getSerializedData())
      .run()
      .catch((error: any) => this.handleError(error))
      .finally(() => this.recaptchaService.reset());

    this.lastFormData = formData;
  }

  public storeCartStep() {
    return {
      save: () => {
        sessionStorage.setItem('cartStep', window['STEP']);
      },
      get: () => {
        let data = JSON.parse(sessionStorage.getItem('cartStep'));

        if (typeof data === 'string') {
          data = JSON.parse(data);
        }

        return data;
      },
      clear: () => {
        sessionStorage.removeItem('cartStep');
      }
    };
  }

  public storeFormData() {
    return {
      save: () => {
        const extendedResolveData = [...this.resolveData()];

        if (this.idCardType) {
          extendedResolveData.push(
            {
              name: 'idCardType',
              value: this.idCardType
            }
          );
        }

        const data = JSON.stringify(extendedResolveData);

        sessionStorage.setItem('formInputData', JSON.stringify(data));
      },
      get: () => {
        let data = JSON.parse(sessionStorage.getItem('formInputData'));

        if (typeof data === 'string') {
          data = JSON.parse(data);
        }

        return data;
      },
      clear: () => {
        sessionStorage.removeItem('formInputData');
      }
    };
  }

  public allowNipNumber(nip: string): Promise<any> {
    return new Promise((resolve, reject) => {
      this.endpoint('checkout.nip', {nip})
        .run()
        .then((response: INipBlacklisted) => {
          resolve(response.blacklisted);
        })
        .catch(() => {
          reject();
        });
    });
  }

  private getSerializedData(): string {
    const data = this.resolveData();

    const str = data.reduce((previousValue, currentValue) => {
      previousValue.push(currentValue.name + '=' + encodeURIComponent(currentValue.value));

      return previousValue;
    }, []).join('&');

    return str;
  }

  private handleError(response: any): void {
    const errors = response && response.validationErrors;

    if (errors) {
      for (let i = 0; i < errors.length; i++) {
        if (errors[i].fieldName === 'redirect') {
          window.location.href = errors[i].errorMessage;
          return;
        }

        if (errors[i].fieldName === '') {
          this.showErrorPage();
        }
      }
    } else {
      this.showErrorPage();
    }
  }


  public showErrorPage(): void {
    window.location.replace('/cart/multi/error');
  }

  private showModal(): void {
    setTimeout(() => {
      if (this.commonInfo.isSecondPage) {
        this.loadersService.start(
          this.loadersService.loaderActions.MODAL_LOADER,
          {
            templateName: CommonModalTemplatesEnum.CREDIT_CHECK_LOADER,
            message: 'Trwa weryfikacja danych',
            preventClose: true,
            isVirginBrand: this.commonInfo.isVirginBrand
          },
          true
        );
      } else {
        this.loadersService.start(
          this.loadersService.loaderActions.MODAL_LOADER,
          {
            message: 'Trwa składanie zamówienia',
            preventClose: true,
            isVirginBrand: this.commonInfo.isVirginBrand
          },
          true
        );
      }
    }, 600);
  }
}
